import { useEffect, useState } from "react";
import api, { formatRand } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ViewHeader, EmptyState, formatDate } from "@/components/admin/AdminShared";

const OrdersTable = ({ orders, testId }) => (
  <div className="bg-white rounded-2xl border border-purple-100 overflow-x-auto">
    <Table data-testid={testId}>
      <TableHeader>
        <TableRow>
          <TableHead>Order</TableHead>
          <TableHead>Client</TableHead>
          <TableHead>Date</TableHead>
          <TableHead>Items</TableHead>
          <TableHead>Payment</TableHead>
          <TableHead className="text-right">Total</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {orders.map((o) => (
          <TableRow key={o.id} data-testid={`revenue-order-${o.order_number}`}>
            <TableCell className="font-semibold text-sm text-purple-950">{o.order_number}</TableCell>
            <TableCell className="text-sm">{o.shipping?.full_name}<p className="text-xs text-muted-foreground">{o.user_email}</p></TableCell>
            <TableCell className="text-sm">{formatDate(o.created_at)}</TableCell>
            <TableCell className="text-sm">{o.items.reduce((n, i) => n + i.quantity, 0)}</TableCell>
            <TableCell className="text-xs">{o.payment_status}</TableCell>
            <TableCell className="text-right font-semibold text-sm">{formatRand(o.total)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

const Summary = ({ label, value, testId }) => (
  <div className="bg-white rounded-2xl border border-purple-100 p-5">
    <p className="text-sm text-muted-foreground">{label}</p>
    <p data-testid={testId} className="text-2xl font-bold text-purple-950 mt-1">{value}</p>
  </div>
);

export const RevenueView = ({ stats, onBack }) => {
  const [orders, setOrders] = useState(null);
  useEffect(() => {
    api.get("/orders").then((r) => setOrders(r.data));
  }, []);

  const paid = (orders || []).filter((o) => o.payment_status === "Paid");
  const unpaid = (orders || []).filter((o) => o.payment_status !== "Paid");

  return (
    <div data-testid="admin-revenue-view">
      <ViewHeader onBack={onBack} title="Revenue" subtitle="Revenue counts paid orders only. Orders still awaiting payment are listed separately." />
      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <Summary label="Paid revenue" value={formatRand(stats?.revenue)} testId="revenue-paid-total" />
        <Summary label="Paid orders" value={stats?.paid_orders ?? "—"} testId="revenue-paid-count" />
        <Summary label="Awaiting payment" value={formatRand(stats?.awaiting_revenue)} testId="revenue-awaiting-total" />
      </div>

      <h3 className="font-semibold text-lg text-purple-950 mb-3">Paid orders ({paid.length})</h3>
      {paid.length ? <OrdersTable orders={paid} testId="revenue-paid-table" /> : <EmptyState>No paid orders yet.</EmptyState>}

      <h3 className="font-semibold text-lg text-purple-950 mt-10 mb-3">Not yet paid ({unpaid.length})</h3>
      {unpaid.length ? <OrdersTable orders={unpaid} testId="revenue-unpaid-table" /> : <EmptyState>Nothing awaiting payment.</EmptyState>}
    </div>
  );
};
